import React from 'react';

interface ConjunctionData {
  pId: number;
  pName: string;
  sId: number;
  sName: string;
  tcaTime?: string | null;
  dca?: number | null;
  poc?: number | null;
  CDM_ID?: string;
}

const formatTCATime = (dateString: string | undefined | null): string => {
  if (!dateString) {
    return 'N/A';
  }

  const normalizedDateString = dateString.split('\n').join('T');
  const date = new Date(normalizedDateString);

  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = months[date.getMonth()];
  const day = date.getDate();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${month} ${day}, ${hours}:${minutes}:${seconds}`;
};

interface ConjunctionCardListProps {
  data: ConjunctionData[];
  page: number;
  limit: number;
  isLoading: boolean;
}

const ConjunctionCard: React.FC<{
  data: ConjunctionData;
  index: number;
}> = ({ data, index }) => {
  const cardBackground = (index + 1) % 2 === 0 ? 'bg-[#3B3B3B]' : 'bg-black';
  const onCardClick = () => {
    window.open(
      `https://platform.spacemap42.com/?service=conjunction&board=false&cdm_id=${data.CDM_ID}`,
      '_blank'
    );
  };

  return (
    <div
      className={`${cardBackground} border border-[#4B4B4B] rounded-lg flex cursor-pointer hover:bg-gray-900 transition-colors`}
      onClick={onCardClick}
    >
      {/* Rank Number */}
      <div className='flex items-center justify-center text-white font-bold !text-xs min-w-[32px] border-r border-[#4B4B4B]'>
        {index + 1}
      </div>

      {/* Content */}
      <div className='flex-1 flex flex-col gap-2 px-3'>
        {/* Primary and Secondary Header */}
        <div className='flex flex-col'>
          <div className='grid grid-cols-2 rounded-tr-md items-start justify-start'>
            <div className='text-[#FFFFFF99] !text-sm font-semibold py-1 text-left'>
              Primary
            </div>
            <div className='text-[#FFFFFF99] !text-sm font-semibold py-1 text-left'>
              Secondary
            </div>
          </div>
          <div className='grid grid-cols-2'>
            <div className='text-white font-bold !text-xs text-left'>
              {data.pName}
            </div>
            <div className='text-white font-bold !text-xs text-left'>
              {data.sName}
            </div>
          </div>
        </div>
        <div className='border border-[#4B4B4B] rounded-lg'></div>

        {/* Data Row */}
        <div className='flex flex-col'>
          <div className='grid grid-cols-[1.6fr_1fr_1fr] gap-2 py-1'>
            <div className='text-[#FFFFFF99] !text-sm font-semibold text-left'>
              TCA(UTC)
            </div>
            <div className='text-[#FFFFFF99] !text-sm font-semibold text-left'>
              DCA(km)
            </div>
            <div className='text-[#FFFFFF99] !text-sm font-semibold text-left'>
              POC_MAX
            </div>
          </div>
          <div className='grid grid-cols-[1.6fr_1fr_1fr] gap-2 pb-1'>
            <div className='text-white font-bold !text-xs text-left whitespace-nowrap'>
              {formatTCATime(data.tcaTime)}
            </div>
            <div className='text-white font-bold !text-xs text-left'>
              {data.dca != null && !isNaN(data.dca)
                ? data.dca.toFixed(3)
                : 'N/A'}
            </div>
            <div className='text-white font-bold !text-xs text-left'>
              {data.poc != null && !isNaN(data.poc)
                ? data.poc.toExponential(2)
                : 'N/A'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConjunctionCardList: React.FC<ConjunctionCardListProps> = ({
  data,
  page,
  limit,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <div className='flex justify-center items-center py-8'>
        <div className='text-white'>Loading...</div>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-2 w-full'>
      {data.map((item, index) => (
        <ConjunctionCard
          key={`${item.CDM_ID}-${index}`}
          data={item}
          index={page * limit + index}
        />
      ))}
    </div>
  );
};

export default ConjunctionCardList;
